/**
 * Suitability map (constraint + opportunity overlay) — for one polygon
 * layer, lets the user mark numeric columns as either:
 *
 *   • Opportunity — contributes `weight × normalized(value)` to the score.
 *     Direction toggle controls whether higher is better or lower is better.
 *   • Constraint  — multiplies score by 0 (fully excluded) when value > 0.
 *
 * The combined score is computed inside a MapLibre expression so it
 * stays in sync with the live source data — no need to rewrite features.
 * A graduated fill ramp is then applied through `userLayerManager.applyGraduated`,
 * the same path the Style modal uses.
 */
import { sampleRamp, stepExpression } from '../ui/colorRamps.js';
import { setLegend } from '../ui/legend.js';
import { setStyle } from '../ui/styleStore.js';
import { toast } from '../ui/toast.js';

const NUMERIC_DTYPES = ['int8','int16','int32','int64','float32','float64','bool'];
const isNumeric = (dt) => NUMERIC_DTYPES.some(t => (dt || '').includes(t));

let userLayersRef = null;
export function registerUserLayers(m) { userLayersRef = m; }

// ---------------------------------------------------------------------------
// Public action
// ---------------------------------------------------------------------------

export function openSuitabilityModal() {
  const visible = userLayersRef?.getVisibleVectorLayersWithFeatures?.() || [];
  const polygons = visible.filter(l => /Polygon/i.test(l.geometryType || ''));
  if (polygons.length === 0) {
    toast('Add a visible polygon layer first.');
    return;
  }

  document.querySelector('#suitabilityModal')?.remove();
  document.body.insertAdjacentHTML('beforeend', shellHtml(polygons));
  const root = document.getElementById('suitabilityModal');

  // Initial state
  const state = { layerId: polygons[0].id, factors: [] };
  rebuildFactors(state, polygons);
  renderFactors(state, polygons);

  root.querySelector('#suLayer').addEventListener('change', e => {
    state.layerId = +e.target.value;
    rebuildFactors(state, polygons);
    renderFactors(state, polygons);
  });
  root.addEventListener('click', e => {
    if (e.target === root || e.target.closest('[data-su="cancel"]')) root.remove();
    if (e.target.closest('[data-su="apply"]')) applySuitability(state, polygons, root);
  });
  document.addEventListener('keydown', escClose, { once: true });
  function escClose(e) { if (e.key === 'Escape' && document.getElementById('suitabilityModal')) root.remove(); }
}

// ---------------------------------------------------------------------------
// State + rendering
// ---------------------------------------------------------------------------

function findLayer(polygons, id) {
  return polygons.find(p => p.id === id);
}

function numericColumnsFromFeatures(features) {
  const sample = features.slice(0, 50);
  const props = sample[0]?.properties || {};
  const cols = [];
  for (const key of Object.keys(props)) {
    if (key === '_fid' || key === 'geometry') continue;
    const isAllNumeric = sample.every(f => {
      const v = f.properties?.[key];
      return v == null || v === '' || (typeof v === 'number') || !Number.isNaN(Number(v));
    });
    const hasAtLeastOneNumber = sample.some(f => {
      const v = f.properties?.[key];
      return v != null && v !== '' && !Number.isNaN(Number(v));
    });
    if (isAllNumeric && hasAtLeastOneNumber) cols.push(key);
  }
  return cols;
}

function rebuildFactors(state, polygons) {
  const lyr = findLayer(polygons, state.layerId);
  if (!lyr) { state.factors = []; return; }
  const cols = numericColumnsFromFeatures(lyr.features);
  state.factors = cols.map(col => ({
    column:    col,
    role:      'skip',     // 'skip' | 'opportunity' | 'constraint'
    weight:    1,
    direction: 'higher',   // 'higher' (higher = better) | 'lower' (lower = better)
  }));
}

function renderFactors(state, polygons) {
  const lyr = findLayer(polygons, state.layerId);
  const host = document.getElementById('suFactors');
  if (!lyr) { host.innerHTML = ''; return; }

  // Compute min/max per column from cached features so we can show ranges.
  const stats = {};
  for (const f of state.factors) {
    let lo = Infinity, hi = -Infinity, n = 0;
    for (const feat of lyr.features) {
      const v = Number(feat.properties?.[f.column]);
      if (!Number.isFinite(v)) continue;
      if (v < lo) lo = v;
      if (v > hi) hi = v;
      n++;
    }
    stats[f.column] = { min: lo, max: hi, count: n };
  }

  if (state.factors.length === 0) {
    host.innerHTML = `<div class="su-empty">No numeric columns on this layer.</div>`;
    return;
  }

  host.innerHTML = state.factors.map((f, i) => {
    const s = stats[f.column];
    const range = Number.isFinite(s.min) ? `${fmt(s.min)} – ${fmt(s.max)}` : 'no values';
    return `
      <div class="su-row" data-i="${i}">
        <div class="su-col">
          <div class="su-col-name">${escape(f.column)}</div>
          <div class="su-col-range">${range}</div>
        </div>
        <select class="su-role" data-su-field="role">
          <option value="skip"        ${f.role === 'skip' ? 'selected' : ''}>Skip</option>
          <option value="opportunity" ${f.role === 'opportunity' ? 'selected' : ''}>Opportunity</option>
          <option value="constraint"  ${f.role === 'constraint' ? 'selected' : ''}>Constraint</option>
        </select>
        ${f.role === 'opportunity' ? `
          <select class="su-dir" data-su-field="direction" title="Higher value = more suitable, or lower?">
            <option value="higher" ${f.direction === 'higher' ? 'selected' : ''}>Higher better</option>
            <option value="lower"  ${f.direction === 'lower'  ? 'selected' : ''}>Lower better</option>
          </select>
          <div class="su-w">
            <input type="range" min="1" max="5" step="1" value="${f.weight}" data-su-field="weight">
            <span class="su-w-val">w=${f.weight}</span>
          </div>
        ` : f.role === 'constraint' ? `
          <span class="su-cnote">excluded when value > 0</span>
        ` : `
          <span class="su-cnote" style="opacity:0.45">ignored</span>
        `}
      </div>
    `;
  }).join('');

  // Bind row inputs
  host.querySelectorAll('.su-row').forEach(row => {
    const i = +row.dataset.i;
    row.querySelectorAll('[data-su-field]').forEach(el => {
      el.addEventListener('input', () => {
        const field = el.dataset.suField;
        const f = state.factors[i];
        if (field === 'role')      f.role = el.value;
        if (field === 'direction') f.direction = el.value;
        if (field === 'weight')    f.weight = +el.value;
        renderFactors(state, polygons);
      });
    });
  });
}

// ---------------------------------------------------------------------------
// Apply
// ---------------------------------------------------------------------------

function applySuitability(state, polygons, root) {
  const lyr = findLayer(polygons, state.layerId);
  if (!lyr) return;

  const opportunities = state.factors.filter(f => f.role === 'opportunity' && f.weight > 0);
  const constraints   = state.factors.filter(f => f.role === 'constraint');

  if (opportunities.length === 0 && constraints.length === 0) {
    toast('Mark at least one column as opportunity or constraint.');
    return;
  }

  // Per-column min/max from cached features (used to normalise contributions).
  const stats = {};
  for (const f of [...opportunities, ...constraints]) {
    let lo = Infinity, hi = -Infinity;
    for (const feat of lyr.features) {
      const v = Number(feat.properties?.[f.column]);
      if (!Number.isFinite(v)) continue;
      if (v < lo) lo = v;
      if (v > hi) hi = v;
    }
    stats[f.column] = { min: lo, max: hi };
  }

  // Build a MapLibre expression for the score.
  // Opportunity term: weight * normalized(value); range 0..weight.
  // Constraint term:  1 if value <= 0 (allowed), 0 otherwise (excluded).
  // Final score = (sum opp terms) * (product of constraint terms).
  // Range: 0 .. totalWeight.
  const totalWeight = opportunities.reduce((s, o) => s + o.weight, 0) || 1;

  const oppTerms = opportunities.map(o => {
    const { min, max } = stats[o.column];
    const span = (max - min) || 1;
    // normalized = (value - min) / span; flip if "lower better".
    const value = ['to-number', ['get', o.column], 0];
    const norm  = o.direction === 'higher'
      ? ['/', ['-', value, min], span]
      : ['/', ['-', max, value], span];
    // clamp to [0, 1] to be safe with values outside the cached range
    const clamped = ['max', 0, ['min', 1, norm]];
    return ['*', o.weight, clamped];
  });

  const sumExpr = oppTerms.length === 0 ? 0
    : oppTerms.length === 1 ? oppTerms[0]
    : ['+', ...oppTerms];

  const constraintTerms = constraints.map(c => {
    // 1 if value is null OR <= 0; else 0.
    const value = ['to-number', ['get', c.column], 0];
    return ['case', ['<=', value, 0], 1, 0];
  });

  const blockedFactor = constraintTerms.length === 0 ? 1
    : constraintTerms.length === 1 ? constraintTerms[0]
    : ['*', ...constraintTerms];

  const scoreExpr = ['*', blockedFactor, sumExpr];

  // Bin the score into 5 quintiles of [0, totalWeight] and assign a green ramp.
  const bins = 5;
  const breaks = Array.from({ length: bins - 1 }, (_, i) => totalWeight * (i + 1) / bins);
  const colors = sampleRamp('green', bins);

  // Wrap our expression into a step expression that uses it as the input.
  const colorExpr = ['step', scoreExpr, colors[0]];
  for (let i = 0; i < breaks.length; i++) {
    colorExpr.push(breaks[i], colors[i + 1]);
  }

  const ok = userLayersRef.applyGraduated(lyr.id, { colorExpr });
  if (!ok) { toast('Layer not on the map.'); return; }

  // Stash the recipe so it survives basemap/style switches.
  setStyle(lyr.id, {
    type: 'suitability',
    factors: state.factors,
    totalWeight,
  });

  // Legend with bin labels (low → high suitability).
  const fmtN = (n) => Number.isInteger(n) ? n : Number(n).toFixed(2);
  const items = colors.map((c, i) => {
    const lo = i === 0 ? 0 : breaks[i - 1];
    const hi = i === colors.length - 1 ? totalWeight : breaks[i];
    return { color: c, label: `${fmtN(lo)} – ${fmtN(hi)}` };
  });
  setLegend(`${lyr.name} · suitability`, items);

  toast(`Suitability applied to "${lyr.name}" · ${opportunities.length} opp · ${constraints.length} con`);
  root.remove();
}

// ---------------------------------------------------------------------------
// Modal HTML
// ---------------------------------------------------------------------------

function shellHtml(polygons) {
  return `
    <div class="modal-bg show" id="suitabilityModal">
      <div class="modal" style="width:640px;max-width:96vw;max-height:90vh;display:flex;flex-direction:column;padding:0">
        <div style="padding:18px 22px 12px;border-bottom:1px solid var(--border)">
          <h3 style="margin:0">Constraint &amp; opportunity map</h3>
          <p style="font-size:11.5px;color:var(--text3);margin:6px 0 0;line-height:1.5">
            Combine multiple numeric columns into a single suitability score per polygon. Mark each column as an opportunity (weighted contribution) or a hard constraint (excludes the polygon when violated).
          </p>
        </div>

        <div style="padding:14px 22px 8px">
          <label style="font-size:11px;color:var(--text2);display:block">
            Layer
            <select id="suLayer" style="width:100%;margin-top:4px;padding:8px 10px;border-radius:6px;border:1px solid var(--border);background:var(--bg3);color:var(--text);font-size:13px">
              ${polygons.map(l => `<option value="${l.id}">${escape(l.name)}</option>`).join('')}
            </select>
          </label>
        </div>

        <div id="suFactors" style="flex:1;overflow:auto;padding:6px 22px 14px"></div>

        <div style="padding:12px 22px;border-top:1px solid var(--border);display:flex;justify-content:flex-end;gap:8px">
          <button class="btn" data-su="cancel">Cancel</button>
          <button class="btn btn-primary" data-su="apply">Apply</button>
        </div>
      </div>
      <style>
        .su-empty { color:var(--text3); font-style:italic; padding:20px 4px; font-size:12.5px; }
        .su-row { display:grid; grid-template-columns: 1fr 130px 130px 110px; gap:8px; align-items:center; padding:8px 0; border-bottom:1px solid var(--border); }
        .su-col-name { font-size:12.5px; font-weight:500; color:var(--text); }
        .su-col-range { font-size:10.5px; color:var(--text3); font-family:var(--mono); margin-top:1px; }
        .su-role, .su-dir { padding:5px 8px; background:var(--bg3); border:1px solid var(--border); border-radius:4px; color:var(--text); font-size:11.5px; }
        .su-w { display:flex; align-items:center; gap:6px; font-size:10.5px; color:var(--text3); font-family:var(--mono); }
        .su-w input[type=range] { flex:1; }
        .su-cnote { font-size:10.5px; color:var(--text3); font-style:italic; font-family:var(--font-display); grid-column: 3 / span 2; }
      </style>
    </div>
  `;
}

function escape(s) {
  return String(s ?? '').replace(/[&<>"]/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;' }[c]));
}

function fmt(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  return Number.isInteger(n) ? n.toLocaleString() : Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 });
}
